import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {DxPopupComponent} from 'devextreme-angular/ui/popup';
import {DetectMethodChanges, isVoid, jsonCloneObject} from 'projects/shared-components/utils';
import {HedgePosition} from "../../data-model/hedge-position";
import * as Enumerable from "linq";
import {HedgePositionsService, HgAfterStateFilter} from "../hedge-positions/hedge-positions.service";
import {ApgPortfolio} from "../../../adjustment-pricing-grid/model/ApgPortfolio";
import {
    GenericConfirmationDialogComponent
} from "../../../generic-confirmation-dialog/generic-confirmation-dialog.component";
import {ToastrService} from "ngx-toastr";
import {TradingInstrumentDisplayNameService} from "../../../trading-instruments/trading-instrument-display-name.service";
import {MessageBusService} from "../../../message-bus.service";

export interface AfterStatePreviewRowTransaction {
    qty?: number;
    operation?: 'Buy' | 'Sell';
    type: 'Call' | 'Put'
    expiration?: string;
    strike?: number;
}

export interface AfterStatePreviewRow {
    hedgeId: string;
    label?: string;
    color?: string;
    underlying?: string;
    underlyingDisplayName?: string;
    before: HedgePosition[];
    after: HedgePosition[];
    transaction: AfterStatePreviewRowTransaction[];
    deferred: boolean;
    removed: boolean;
}

export interface AfterStateViewConfig {
    adjustmentDescriptor: string,
    positions: HedgePosition[],
    portfolio: ApgPortfolio,
    slot: 'first' | 'second',
    side: 'Calls' | 'Puts'
}

@Component({
    selector: 'ets-hg-after-state-preview',
    templateUrl: './hg-after-state-preview.component.html',
    styleUrls: [
        './hg-after-state-preview.component.scss',
        '../../hedging-grid-common-styles.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgAfterStatePreviewComponent implements OnInit {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _toastr: ToastrService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService,
        private readonly _messageBus: MessageBusService,
    ) {
    }

    private _resolver: (value: AfterStatePreviewRow[] | PromiseLike<AfterStatePreviewRow[]>) => void;
    private _rejector: (reason?: any) => void;

    @ViewChild(DxPopupComponent) popup: DxPopupComponent;

    title = "After State Preview";

    width = 1030;

    get height(): number {

        let value = 400;

        if (this.previewRows.length > 1) {
            value = 560;
        }

        return value;

    }

    portfolio: ApgPortfolio;

    slot: 'first' | 'second';

    afterState: HedgePosition[];

    side: 'Call' | 'Put';

    adjustment: string;

    btnApplyText = 'Apply';

    btnCloseText = 'Close';

    previewRows: AfterStatePreviewRow[] = [];

    esHedgingEnabled = false;

    @ViewChild(GenericConfirmationDialogComponent)
    confirmationDialog: GenericConfirmationDialogComponent;

    ngOnInit(): void {
        this._messageBus.of<{ enabled: boolean }>('Hg.EsHedgingModeChanged')
            .subscribe(msg => {
                this.esHedgingEnabled = msg.payload.enabled;
                this._changeDetector.detectChanges();
            });
    }

    @DetectMethodChanges()
    async show(config: AfterStateViewConfig): Promise<AfterStatePreviewRow[]> {

        this.portfolio = config.portfolio;
        this.slot = config.slot;
        this.afterState = config.positions;
        this.adjustment = config.adjustmentDescriptor;
        this.side = config.side === 'Calls' ? 'Call' : 'Put';

        await this.setupRows();

        this.popup.visible = true;

        setTimeout(() => this._changeDetector.detectChanges());

        return new Promise<AfterStatePreviewRow[]>((res, rej) => {
            this._resolver = res;
            this._rejector = rej;
        });
    }

    onApplyClicked() {

        // this.updateFilter();

        if (this._resolver) {
            this._resolver(this.previewRows.slice());
        }

        this.closePreview();
    }

    onCloseClicked() {
        if (this._rejector) {
            this._rejector();
        }
        this.closePreview();
    }

    @DetectMethodChanges()
    private closePreview() {
        this._resolver = null;
        this._rejector = null;
        this.popup.visible = false;
    }

    private async setupRows() {
        const state = Enumerable.from(this.afterState);

        console.log('after state', this.afterState);

        let currentPositions = await this._hedgePositionsService
            .getHedgePositions(this.portfolio);

        currentPositions = currentPositions.filter(x => x.type === this.side);

        console.log('current positions', currentPositions);

        const currentHedges = Enumerable.from(currentPositions);

        const afterStateById = state
            .groupBy(x => x.groupId)
            .select(x => {
                const items = x.toArray();
                const label = items[0].label;
                const color = items[0].color;

                return {
                    id: x.key(),
                    label,
                    color,
                    items
                }
            }).toArray();

        const currentStateById = currentHedges
            .groupBy(x => x.groupId)
            .select(x => {
                const items = x.toArray();
                const label = items[0].label;
                const color = items[0].color;

                return {
                    id: x.key(),
                    label,
                    color,
                    items
                }
            }).toArray();

        const newHedges = afterStateById.filter(grp =>
            currentStateById.every(x => x.id !== grp.id));

        const removedHedges = currentStateById.filter(grp =>
            afterStateById.every(x => x.id !== grp.id));

        const changedHedges = currentStateById.filter(grp =>
            afterStateById.some(x => x.id === grp.id));

        const newHedgeRows = newHedges.map(newHedge => {
            const before = [];

            const transaction: AfterStatePreviewRowTransaction[] = newHedge.items.map(item => {
                const t: AfterStatePreviewRowTransaction = {
                    strike: item.strike,
                    expiration: item.expiration,
                    operation: item.qty > 0 ? 'Buy' : 'Sell',
                    qty: item.qty,
                    type: item.type,
                };
                return t;
            });

            const after = jsonCloneObject(newHedge.items);

            const ul = this.resolveUnderlying(newHedge.items);

            const row: AfterStatePreviewRow = {
                hedgeId: newHedge.id,
                label: newHedge.label,
                color: newHedge.color,
                underlying: ul.underlying,
                underlyingDisplayName: ul.underlyingDisplayName,
                before,
                after,
                transaction,
                deferred: false,
                removed: false
            };


            return row;
        });

        console.log('new hedges rows', newHedgeRows);

        const removedHedgeRows = removedHedges.map(removedHedge => {
            const before = jsonCloneObject(removedHedge.items);

            const transaction: AfterStatePreviewRowTransaction[] = removedHedge.items.map(item => {
                const t: AfterStatePreviewRowTransaction = {
                    strike: item.strike,
                    expiration: item.expiration,
                    operation: item.qty > 0 ? 'Sell' : 'Buy',
                    qty: item.qty * -1,
                    type: item.type,
                };
                return t;
            });

            const after = [];

            const ul = this.resolveUnderlying(removedHedge.items);

            const row: AfterStatePreviewRow = {
                hedgeId: removedHedge.id,
                label: removedHedge.label,
                color: removedHedge.color,
                underlying: ul.underlying,
                underlyingDisplayName: ul.underlyingDisplayName,
                before,
                after,
                transaction,
                deferred: false,
                removed: false
            };

            return row;
        });

        console.log('removed hedges rows', newHedgeRows);

        const changedHedgesRows = changedHedges.map(changedHedge => {

            const originalHedge = currentStateById
                .find(grp => grp.id === changedHedge.id)?.items || [];

            const destinationHedge = afterStateById
                .find(grp => grp.id === changedHedge.id)?.items || [];

            const newLegs = destinationHedge.filter(x =>
                originalHedge.findIndex(xx => xx.ticker === x.ticker) === -1)
                .map(x => x.ticker);

            const removedLegs = originalHedge.filter(ol =>
                destinationHedge.findIndex(dl => dl.ticker === ol.ticker) === -1)
                .map(x => x.ticker);

            const changedLegs = destinationHedge.filter(dleg =>
                originalHedge.findIndex(oleg => oleg.ticker === dleg.ticker && oleg.qty !== dleg.qty) >= 0)
                .map(x => x.ticker);

            const newTrans: AfterStatePreviewRowTransaction[] = newLegs.map(newLeg => {
                const leg = destinationHedge.find(x => x.ticker === newLeg);
                return {
                    operation: leg.qty > 0 ? 'Buy' : 'Sell',
                    expiration: leg.expiration,
                    qty: leg.qty,
                    strike: leg.strike,
                    type: leg.type,
                };
            });

            const removeTrans: AfterStatePreviewRowTransaction[] = removedLegs.map(removedLeg => {
                const leg = originalHedge.find(x => x.ticker === removedLeg);
                return {
                    operation: leg.qty > 0 ? 'Sell' : 'Buy',
                    expiration: leg.expiration,
                    qty: leg.qty * -1,
                    strike: leg.strike,
                    type: leg.type
                };
            });

            const changeTrans = changedLegs.map(changedLeg => {
                const originalLeg = originalHedge.find(x => x.ticker === changedLeg);
                const destinationLeg = destinationHedge.find(x => x.ticker === changedLeg);

                console.assert(destinationLeg.strike === originalLeg.strike);
                console.assert(destinationLeg.expiration === originalLeg.expiration);

                const qty = destinationLeg.qty - originalLeg.qty;

                const tr: AfterStatePreviewRowTransaction = {
                    strike: destinationLeg.strike,
                    expiration: destinationLeg.expiration,
                    qty: qty,
                    operation: qty > 0 ? 'Buy' : 'Sell',
                    type: destinationLeg.type,
                }

                return tr;
            });

            const before = originalHedge;
            const after = destinationHedge;
            const transaction = newTrans.concat(removeTrans).concat(changeTrans)
                .sort((a, b) => b.strike - a.strike);

            const ul = this.resolveUnderlying(destinationHedge.length > 0 ? destinationHedge : originalHedge);

            const row: AfterStatePreviewRow = {
                hedgeId: destinationHedge[0].groupId,
                label: destinationHedge[0].label,
                color: destinationHedge[0].color,
                underlying: ul.underlying,
                underlyingDisplayName: ul.underlyingDisplayName,
                before,
                after,
                transaction,
                deferred: false,
                removed: false
            };

            return row;
        });

        console.log('changed hedges rows', changedHedgesRows)

        const totalRows = newHedgeRows.concat(removedHedgeRows).concat(changedHedgesRows);

        this.previewRows = Enumerable.from(totalRows).orderByDescending(x => {
            const positions = isVoid(x.after) ? x.before : x.after;
            const order = positions[0]?.strike;
            return order;
        }).toArray();

        const afterStateFilter = this._hedgePositionsService.getAfterStateFilter(this.portfolio, this.slot);
        if (afterStateFilter) {
            if (!isVoid(afterStateFilter.excluded)) {
                this.previewRows.forEach(row => {
                    row.deferred = afterStateFilter.excluded.includes(row.hedgeId);
                });
            }

            // if (!isVoid(afterStateFilter.removed)) {
            //     this.previewRows.forEach(row => {
            //         row.removed = afterStateFilter.removed.includes(row.hedgeId);
            //     });
            // }

            if (!isVoid(afterStateFilter.applied)) {
                this.previewRows.forEach(row => {
                    row.removed = afterStateFilter.applied.includes(row.hedgeId);
                });
            }
        }
    }

    @DetectMethodChanges()
    toggleHedgeExcluded(row: AfterStatePreviewRow) {
        row.deferred = !row.deferred;
       // this.updateFilter();
    }

    @DetectMethodChanges({isAsync: true})
    async onHedgeRemoveRequest(row: AfterStatePreviewRow) {

        try {
            const text = [
                'Are you sure you want to remove this hedge?',
                `"${row.label}"`
            ];

            await this.confirmationDialog.show(text);

            row.removed = true;

            // this.updateFilter();

            if (this.previewRows.every(row => row.transaction.length === 0 || row.removed)) {
                this._toastr.info('No changes left in "After State"');
                this.onApplyClicked();
            }

        } catch (e) {
            //
        }
    }

    private resolveUnderlying(positions: HedgePosition[]): { underlying?: string, underlyingDisplayName?: string } {
        const asset = positions?.[0]?.asset;
        if (!asset) return {};
        return {
            underlying: asset,
            underlyingDisplayName: this._displayNameService.getDisplayNameForTicker(asset)
        };
    }

    private async getOriginalStateOfTheHedge(hedgeId: string) {
        let currentPositions = await this._hedgePositionsService
            .getHedgePositions(this.portfolio);

        const originalState = currentPositions.filter(p => p.groupId === hedgeId);

        return originalState;
    }

    private updateFilter() {
        const excluded = this.previewRows
            .filter(row => row.deferred)
            .map(row => row.hedgeId);

        const removed = this.previewRows
            .filter(row => row.removed)
            .map(row => row.hedgeId);

        const filterObj: HgAfterStateFilter = {
            excluded,
            removed,
            applied: []
        };

        this._hedgePositionsService.saveAfterStateFilter(
            filterObj,
            this.portfolio,
            this.slot
        );
    }

    canDeferOrCancel(row: AfterStatePreviewRow) {
        return row.transaction.length > 0;
    }
}
